import {
  LEARNING_INSIGHT_PRIORITY_LABEL,
  LEARNING_RESOLUTION_SCOPE,
  LEARNING_SUGGESTION_DECISION,
  LEARNING_SUGGESTION_DOMAIN_DECISION,
} from "@/constants";
import type {
  LlmCallMessage,
  PipelineVerboseStep,
} from "@/features/chat/lib/turnStream";
import { ANSWER_PART_EVENT, ANSWER_VALIDATED_EVENT } from "@/features/chat/lib/turnStream";
import { turnClockFromSteps, type TurnClock } from "@/features/chat/lib/turnClock";
import type { ValueFormat } from "@/shared/lib/valueFormat";

/** Rows that are chunks or early copies of the answer, not pipeline steps. */
const HISTORY_HIDDEN_STEP_TYPES = new Set(["token", ANSWER_PART_EVENT, ANSWER_VALIDATED_EVENT]);
import { isSafeSourceUrl, type CitedSource } from "@/shared/lib/citations";
import type { StreamAnswerParts } from "@/features/chat/lib/answerParts";
import { isReaction, type FeedbackState } from "@/shared/lib/feedbackReaction";

/** The requesting user's own feedback on a conversation turn. `reaction` is null when they
 * have none — but on the read path here, a `feedback` field is only ever present at all when
 * a persisted reaction exists, so in practice this is never constructed with `reaction: null`. */
export type ChatTurnFeedback = FeedbackState;

export type TurnResponseStatus =
  | "pending"
  | "streaming"
  | "waiting_for_clarification"
  | "complete"
  | "error"
  | "terminated";

/** Why the backend ranked a clarification option where it did. */
export type ClarificationOptionReason =
  | "learning_history"
  | "intent_match"
  | "question_match"
  | "name_match";

export type ClarificationOption = {
  id: string;
  label: string;
  description?: string | null;
  option_type: string;
  value?: Record<string, unknown>;
  confidence?: number | null;
  /** Absent on turns ranked before reasons existed. */
  reasons?: ClarificationOptionReason[];
  rank_score?: number | null;
};

export type ClarificationQuestionItem = {
  id: string;
  question: string;
  reason?: string;
  missing_or_ambiguous_terms?: string[];
  options: ClarificationOption[];
  allow_other?: boolean;
};

export type ClarificationAnswerItem = {
  question_id: string;
  selected_option_id: string;
  /** More than one entry only when the user picked "All of them" on a slot. */
  selected_option_ids?: string[];
  custom_text?: string | null;
};

/** A single dropdown entry inside a rewritten-question slot. Options arrive sorted best-first. */
export type ClarificationSlotOption = {
  id: string;
  label: string;
  option_type: string;
  value?: Record<string, unknown>;
  /** Absent on turns ranked before reasons existed. */
  reasons?: ClarificationOptionReason[];
  /** True when this option comes from the user's Learning History. */
  learned?: boolean;
};

/** One ambiguous span of the user's question, rendered as an inline dropdown. */
export type ClarificationSlot = {
  id: string;
  term: string;
  start: number;
  end: number;
  options: ClarificationSlotOption[];
  default_option_id: string;
  allow_multiple?: boolean;
  allow_other?: boolean;
};

/** The user's own sentence with each ambiguous span made selectable. */
export type ClarificationRewrite = {
  source_question: string;
  template: string;
  slots: ClarificationSlot[];
};

export type ClarificationPresentation = "rewrite" | "questions";

export type ClarificationRequest = {
  version?: number;
  /** Legacy turns carry no value; they render as question cards. */
  presentation?: ClarificationPresentation;
  rewrite?: ClarificationRewrite | null;
  clarification_type: "keyword" | "ambiguity" | "follow_up" | "validator";
  source_node: string;
  questions: ClarificationQuestionItem[];
  resume_node?: string;
  // v1 compat
  question?: string;
  reason?: string;
  missing_or_ambiguous_terms?: string[];
  options?: ClarificationOption[];
  allow_other?: boolean;
};

function parseClarificationRewrite(value: unknown): ClarificationRewrite | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const row = value as Record<string, unknown>;
  const template = row.template;
  const sourceQuestion = row.source_question;
  if (typeof template !== "string" || typeof sourceQuestion !== "string") {
    return null;
  }
  const rawSlots = Array.isArray(row.slots) ? row.slots : [];
  const slots = rawSlots.filter(
    (slot): slot is ClarificationSlot =>
      typeof slot === "object" &&
      slot !== null &&
      typeof (slot as ClarificationSlot).id === "string" &&
      typeof (slot as ClarificationSlot).default_option_id === "string" &&
      Array.isArray((slot as ClarificationSlot).options),
  );
  if (slots.length === 0) {
    return null;
  }
  return { source_question: sourceQuestion, template, slots };
}

export function normalizeClarificationRequest(
  value: ClarificationRequest | Record<string, unknown> | null | undefined,
): ClarificationRequest | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const row = value as Record<string, unknown>;
  const rawQuestions = row.questions;
  if (Array.isArray(rawQuestions) && rawQuestions.length > 0) {
    const questions = rawQuestions.filter(
      (item): item is ClarificationQuestionItem =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as ClarificationQuestionItem).id === "string" &&
        typeof (item as ClarificationQuestionItem).question === "string" &&
        Array.isArray((item as ClarificationQuestionItem).options),
    );
    if (questions.length === 0) {
      return null;
    }
    // A rewrite is only honoured when every slot maps to a question that shipped —
    // otherwise the sentence would offer a span the backend cannot resolve.
    const questionIds = new Set(questions.map((question) => question.id));
    const rewrite = parseClarificationRewrite(row.rewrite);
    const usableRewrite =
      rewrite && rewrite.slots.every((slot) => questionIds.has(slot.id)) ? rewrite : null;
    return {
      ...(value as ClarificationRequest),
      version: typeof row.version === "number" ? row.version : 2,
      presentation: usableRewrite ? "rewrite" : "questions",
      rewrite: usableRewrite,
      clarification_type:
        row.clarification_type as ClarificationRequest["clarification_type"],
      source_node: String(row.source_node ?? "unknown"),
      questions,
      resume_node:
        typeof row.resume_node === "string" ? row.resume_node : undefined,
    };
  }

  const question = row.question;
  const options = row.options;
  if (typeof question !== "string" || !Array.isArray(options)) {
    return null;
  }

  return {
    version: typeof row.version === "number" ? row.version : 1,
    presentation: "questions",
    rewrite: null,
    clarification_type:
      row.clarification_type as ClarificationRequest["clarification_type"],
    source_node: String(row.source_node ?? "unknown"),
    questions: [
      {
        id: "q1",
        question,
        reason: typeof row.reason === "string" ? row.reason : undefined,
        missing_or_ambiguous_terms: Array.isArray(
          row.missing_or_ambiguous_terms,
        )
          ? (row.missing_or_ambiguous_terms as string[])
          : undefined,
        options: options as ClarificationOption[],
        allow_other:
          typeof row.allow_other === "boolean" ? row.allow_other : true,
      },
    ],
    resume_node:
      typeof row.resume_node === "string" ? row.resume_node : undefined,
    question,
    reason: typeof row.reason === "string" ? row.reason : undefined,
    options: options as ClarificationOption[],
  };
}

export type ClarificationResponseResult = {
  turn_id: string;
  response_status: "streaming";
  stream_url: string;
};

export type AnswerKind =
  "semantic" | "data" | "chart" | "hybrid" | "out_of_scope";

/** One result set a turn produced: the primary table, or one of the others. */
export type ChatResultTable = {
  /** The question this query answered; absent on the primary result. */
  purpose?: string;
  columns: string[];
  rows: Record<string, unknown>[];
  /** Rows the query returned, which can exceed `rows.length`. */
  row_count?: number;
  truncated?: boolean;
  /** How many of `row_count` this payload actually carries. */
  shown_rows?: number;
};

export type ChatFailedResult = {
  purpose?: string;
  error_message?: string | null;
};

export type ChatTabularData = ChatResultTable & {
  /**
   * The turn's other result sets. An investigation runs a baseline query and then the
   * breakdowns that explain it, and the insights cite the breakdowns — so showing only the
   * primary table hides the data the answer was actually built on.
   */
  additional_results?: ChatResultTable[];
  /** Queries that failed, so an answer built on partial evidence does not read as complete. */
  failed_results?: ChatFailedResult[];
};

export type ChartType =
  | "line"
  | "bar_vertical"
  | "bar_horizontal"
  | "area"
  | "pie"
  | "scatter"
  | "combo";
export type ChartAxisType = "time" | "category" | "number";
export type MeasureEncoding = "line" | "bar";

export type ChartDimension = {
  key: string;
  label?: string | null;
  type?: ChartAxisType;
  /**
   * How this field's values are displayed on the axis, in tooltips and, for a stack dimension, in
   * the legend. Display only; absent shows them as they are. Set in App Studio.
   */
  format?: ValueFormat | null;
};

export type ChartMeasure = {
  key: string;
  label?: string | null;
  encoding?: MeasureEncoding | null;
  predicted?: boolean | null;
  y_axis_index?: 0 | 1 | null;
  /** Bar measures sharing a group stack into one bar per category. */
  stack_group?: string | null;
  /** How this series' values are displayed in tooltips and on its value axis. Display only. */
  format?: ValueFormat | null;
};

export type GeneralizedChartDefinition = {
  id: string;
  title?: string | null;
  description?: string | null;
  dimension: ChartDimension;
  measures: ChartMeasure[];
  recommended_chart_type: ChartType;
  is_trend?: boolean;
  rationale?: string | null;
  stack_dimension?: ChartDimension | null;
};

/** @deprecated Use GeneralizedChartDefinition */
export type ChartDefinition = GeneralizedChartDefinition;

export type CardFormat = "number" | "text";

/**
 * A single-metric card, rendered instead of a chart when a result has only
 * one row (no axis to plot). One card per number/text column; date-like
 * columns never produce a card.
 */
export type CardDefinition = {
  id: string;
  title: string;
  column: string;
  value: unknown;
  format: CardFormat;
};

export type ChatChartCollection = {
  version?: number;
  data: Record<string, unknown>[];
  charts: GeneralizedChartDefinition[];
  cards?: CardDefinition[];
};

/** @deprecated Legacy single-chart shape; prefer ChatChartCollection */
export type ChatChartData = ChatChartCollection;

export type AnswerConfidenceLevel = "high" | "medium" | "low";

export type AnswerConfidence = {
  score: number;
  level: AnswerConfidenceLevel;
  signals: string[];
};

/**
 * How the answer from the domain's uploaded documents related to the warehouse answer.
 * Structured (warehouse) data is always authoritative and carries the summary and insights; what
 * the documents add — including a figure that differs — is shown beside it as extra information.
 */
export type DocumentAgreement =
  "supports" | "complements" | "conflicts" | "document_only" | "not_relevant";

export type DocumentCitation = {
  sourceFile: string;
  page?: number | null;
};

/**
 * One cited file-and-page behind an answer. Richer than the bare filename in `documentSources`:
 * `previewImage` is the retrieved page image itself, as a `data:` URI, present only for a cited
 * passage that was a page image and only for the first couple of them.
 */
export type DocumentSourceRef = {
  sourceFile: string;
  page?: number | null;
  docTitle?: string | null;
  timeline?: string | null;
  isImage: boolean;
  previewImage?: string | null;
};

export type DocumentConflict = {
  subject: string;
  structuredValue: string;
  documentValue: string;
  structuredSource?: string | null;
  documentSources: DocumentCitation[];
  /**
   * Who the document credits its figure to, when the page says — "Newzoo", "NPD Circana". Distinct
   * from `documentSources`, which is the file the number was read out of: a weekly report restating
   * a third party's measurement names both, and only this field says whose measurement it is.
   */
  documentStatedSource?: string | null;
  likelyCause?: string | null;
  /**
   * Both sides measured the same period, rather than the document predating the warehouse. Carried
   * for callers that need to know what the document's figure describes; the UI no longer renders
   * it differently, since the warehouse answer stands either way.
   */
  samePeriod: boolean;
};

export type GeneratedQuery = {
  purpose?: string;
  sql?: string | null;
  status?: string;
  row_count?: number;
  execution_ms?: number | null;
  error_message?: string | null;
  /** True when the query combines measures across multiple physical tables (a composite metric). */
  cross_table_metric?: boolean;
  /** Distinct physical tables a cross-table metric spans. */
  source_tables?: string[];
};

export type LearningHistoryOwnerType = "measure" | "dimension";

/** Another meaning the user can switch an auto-resolved term to. */
export type LearningHistoryAlternative = {
  ownerType: LearningHistoryOwnerType;
  ownerKey: string;
  label: string;
  /** The user's question with the term replaced by this alternative's label. */
  rewrittenQuestion: string;
};

/**
 * A term the backend resolved from the user's Learning History instead of asking a
 * clarification question, surfaced on the answer so the user can change it.
 */
export type LearningHistoryResolution = {
  term: string;
  ownerType: LearningHistoryOwnerType;
  ownerKey: string;
  label: string;
  confidenceLabel: AnswerConfidenceLevel;
  insightId: number;
  domainId: number;
  /** Whose meaning it was: the user's own, or one approved for the whole domain. */
  scope: LearningResolutionScope;
  alternatives: LearningHistoryAlternative[];
};

/**
 * The answer's offer to save a correction or preference the user stated in this message, and
 * what the user chose so far. `decision` answers "save it as my learning?"; `domainDecision`
 * answers "suggest it for the whole domain?", asked only once it is saved.
 */
export type LearningSuggestionDecision =
  (typeof LEARNING_SUGGESTION_DECISION)[keyof typeof LEARNING_SUGGESTION_DECISION];

export type LearningSuggestionDomainDecision =
  (typeof LEARNING_SUGGESTION_DOMAIN_DECISION)[keyof typeof LEARNING_SUGGESTION_DOMAIN_DECISION];

export type LearningSuggestion = {
  statement: string;
  decision: LearningSuggestionDecision | null;
  domainDecision: LearningSuggestionDomainDecision | null;
};

export type LearningResolutionScope =
  (typeof LEARNING_RESOLUTION_SCOPE)[keyof typeof LEARNING_RESOLUTION_SCOPE];

export type AssistantTurnData = {
  text: string;
  summary?: string | null;
  insights?: string[] | null;
  why?: string | null;
  whyPoints?: string[] | null;
  action?: string | null;
  actionPoints?: string[] | null;
  /** Numbered sources the `why` text cites. Absent when research contributed nothing. */
  sources?: CitedSource[];
  businessImpact?: string | null;
  followUpQuestions: string[];
  verboseLog: PipelineVerboseStep[];
  answerKind?: AnswerKind;
  tabularData?: ChatTabularData | null;
  chartData?: ChatChartData | null;
  generatedQueries?: GeneratedQuery[] | null;
  clarificationRequest?: ClarificationRequest | null;
  answerConfidence?: AnswerConfidence | null;
  documentAgreement?: DocumentAgreement | null;
  documentConflicts?: DocumentConflict[];
  documentSources?: string[];
  documentSourceRefs?: DocumentSourceRef[];
  learningHistoryResolutions?: LearningHistoryResolution[];
  learningSuggestion?: LearningSuggestion;
  /**
   * True while the table/chart/SQL shown came from early answer parts the answer validator
   * has not yet approved -- a SQL retry may still replace them. Absent once the turn is done.
   */
  answerPartsProvisional?: boolean;
  /** Summary and insights are shown; why and action are still being written (streaming only). */
  narrativePending?: boolean;
  /** The why research changed business impact after it was first written. */
  businessImpactUpdated?: boolean;
};

export type ConversationTurn = {
  turnId: string;
  turnIndex: number;
  userQuestion: string;
  assistantData: AssistantTurnData | null;
  status: TurnResponseStatus;
  clarificationRequest?: ClarificationRequest | null;
  feedback?: ChatTurnFeedback | null;
  /**
   * When the question was sent (browser clock); drives the elapsed time while it runs, less the
   * clarification waits its `TurnClock` fields record.
   */
  startedAt?: number;
  /** When the answer completed or the turn was cancelled; shown under the response. */
  completedAt?: number;
  /** Parts a still-running turn had already streamed, rebuilt from its stored steps. */
  resumeAnswerParts?: StreamAnswerParts;
} & TurnClock;

export type ChatUserMessage = {
  id: string;
  role: "user";
  content: string;
};

export type FirstTurnResponse = {
  session_id: string;
  turn_id: string;
  turn_index: number;
  status: "pending";
};

export type FollowUpTurnResponse = {
  session_id: string;
  turn_id: string;
  turn_index: number;
  status: "pending";
};

export type TerminateTurnResponse = {
  turn_id: string;
  status: "terminated";
};

export type VerboseStepItem = {
  step_index: number;
  step_type: string;
  step_title: string | null;
  summary: string | null;
  detail: string;
  payload: Record<string, unknown> | null;
  duration_ms?: number | null;
  /** ISO timestamp of when the step was stored. */
  created_at?: string | null;
};

export type SessionTurnItem = {
  turn_id: string;
  turn_index: number;
  user_question: string;
  assistant_response: Record<string, unknown> | string | null;
  tabular_data?: Record<string, unknown> | null;
  chart_data?: Record<string, unknown> | null;
  response_status: string;
  /** ISO timestamp of when the question was asked. */
  created_at?: string | null;
  /** ISO timestamp of when the turn ended: answered, cancelled or failed. */
  completed_at?: string | null;
  clarification_request?: ClarificationRequest | null;
  clarification_response?: Record<string, unknown> | null;
  paused_at_node?: string | null;
  resume_node?: string | null;
  verbose_steps?: VerboseStepItem[];
  feedback?: Record<string, unknown> | null;
};

export type SessionTurnsResponse = {
  session_id: string;
  domain_id: number;
  title: string | null;
  created_at: string;
  /** Who asked the session's questions (its owner), which need not be who is reading it. */
  user_name?: string | null;
  turns: SessionTurnItem[];
};

function parseStringField(
  value: Record<string, unknown>,
  key: string,
): string | null {
  const raw = value[key];
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

function parseStringArray(
  value: Record<string, unknown>,
  key: string,
): string[] | null {
  const raw = value[key];
  if (!Array.isArray(raw)) {
    return null;
  }
  const items = raw.filter(
    (item): item is string =>
      typeof item === "string" && item.trim().length > 0,
  );
  return items.length > 0 ? items : null;
}

/** A source is kept only when it has a usable id and an `http(s)` URL.
 *
 * The backend validates every source before it persists one, so this is the second gate rather
 * than the only one — but the marker renderer turns a source into an `href`, and a renderer
 * should never be the first thing to check what it is about to link to. */
export function parseCitedSources(raw: unknown): CitedSource[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const sources: CitedSource[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) {
      continue;
    }
    const record = item as Record<string, unknown>;
    const id = typeof record.id === "number" ? record.id : Number.NaN;
    const url = typeof record.url === "string" ? record.url : "";
    if (!Number.isInteger(id) || !isSafeSourceUrl(url)) {
      continue;
    }
    sources.push({
      id,
      url,
      title: typeof record.title === "string" ? record.title : undefined,
      publisher:
        typeof record.publisher === "string" ? record.publisher : undefined,
      published_date:
        typeof record.published_date === "string"
          ? record.published_date
          : undefined,
      accessed_date:
        typeof record.accessed_date === "string"
          ? record.accessed_date
          : undefined,
    });
  }
  return sources;
}

function parseFollowUpQuestions(value: Record<string, unknown>): string[] {
  const raw = value.follow_up_questions;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (item): item is string =>
      typeof item === "string" && item.trim().length > 0,
  );
}

function parseResponseText(
  value: Record<string, unknown> | string | null,
): string | null {
  if (value === null) {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value.text === "string") {
    return value.text;
  }
  if (typeof value.response_text === "string") {
    return value.response_text;
  }
  if (typeof value.content === "string") {
    return value.content;
  }
  return null;
}

export function mapVerboseStepItem(
  item: VerboseStepItem,
): PipelineVerboseStep & {
  request?: ClarificationRequest;
} {
  const payload = item.payload ?? {};
  const eventType =
    typeof payload.event_type === "string"
      ? payload.event_type
      : item.step_type;
  const createdAt = item.created_at ? Date.parse(item.created_at) : Number.NaN;
  const step: PipelineVerboseStep & { request?: ClarificationRequest } = {
    stepIndex: item.step_index,
    eventType,
    stepTitle: item.step_title ?? item.step_type,
    summary: item.summary ?? item.step_title ?? item.step_type,
    detail: item.detail,
    ...(typeof item.duration_ms === "number" ? { durationMs: item.duration_ms } : {}),
    ...(typeof payload.elapsed_ms === "number" ? { elapsedMs: payload.elapsed_ms } : {}),
    ...(Number.isNaN(createdAt) ? {} : { createdAt }),
  };
  const request = parseClarificationRequest(
    payload.request as
      ClarificationRequest | Record<string, unknown> | undefined,
  );
  if (request) {
    step.request = request;
  }
  if (Array.isArray(payload.messages)) {
    step.messages = payload.messages.filter(
      (message): message is LlmCallMessage =>
        typeof message === "object" &&
        message !== null &&
        typeof (message as LlmCallMessage).role === "string" &&
        typeof (message as LlmCallMessage).content === "string",
    );
  }
  if (typeof payload.raw_response === "string") {
    step.rawResponse = payload.raw_response;
  }
  if (payload.input && typeof payload.input === "object") {
    step.input = payload.input as Record<string, unknown>;
  }
  if (payload.output && typeof payload.output === "object") {
    step.output = payload.output as Record<string, unknown>;
  }
  // The payload copy is never trimmed, so it is what the panel renders -- older turns stored a
  // 500-character preview in the detail body.
  if (typeof payload.sql === "string" && payload.sql.length > 0) {
    step.sql = payload.sql;
  }
  return step;
}

function parseAnswerKind(
  value: Record<string, unknown>,
): AnswerKind | undefined {
  const raw = value.answer_kind;
  if (typeof raw !== "string") {
    return undefined;
  }
  const normalized = raw.trim().toLowerCase();
  if (
    normalized === "semantic" ||
    normalized === "data" ||
    normalized === "chart" ||
    normalized === "hybrid" ||
    normalized === "out_of_scope"
  ) {
    return normalized;
  }
  return undefined;
}

export function parseTabularDataFromRaw(raw: unknown): ChatTabularData | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  return parseTabularData({ tabular_data: raw });
}

/** Tolerates a missing/null `feedback` so turns still parse against a backend that predates
 * the feedback field. A `feedback` field is only ever present when a reaction was actually
 * recorded (see `_graft_turn_feedback` on the backend), so an invalid shape here is dropped
 * rather than trusted. */
function parseChatTurnFeedbackFromRaw(raw: unknown): ChatTurnFeedback | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const row = raw as Record<string, unknown>;
  if (!isReaction(row.reaction)) {
    return null;
  }
  return {
    reaction: row.reaction,
    comment: typeof row.comment === "string" ? row.comment : null,
  };
}

export function parseChartCollectionFromRaw(raw: unknown): ChatChartCollection | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (Array.isArray(record.charts)) {
    const charts = record.charts
      .map((item) => parseGeneralizedChartDefinition(item))
      .filter((item): item is GeneralizedChartDefinition => item !== null);
    const cards = parseCardDefinitions(record.cards);
    if (charts.length === 0 && cards.length === 0) {
      return null;
    }
    return {
      version: typeof record.version === "number" ? record.version : 2,
      data: extractSharedData(record, record.charts),
      charts,
      ...(cards.length > 0 ? { cards } : {}),
    };
  }
  return parseLegacyChartData(record);
}

function parseResultTable(raw: unknown): ChatResultTable | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const columns = Array.isArray(record.columns)
    ? record.columns.filter((item): item is string => typeof item === "string")
    : [];
  const rows = Array.isArray(record.rows)
    ? record.rows.filter(
        (item): item is Record<string, unknown> =>
          typeof item === "object" && item !== null,
      )
    : [];
  if (columns.length === 0 && rows.length === 0) {
    return null;
  }
  return {
    ...(typeof record.purpose === "string" ? { purpose: record.purpose } : {}),
    columns,
    rows,
    row_count:
      typeof record.row_count === "number" ? record.row_count : rows.length,
    truncated:
      typeof record.truncated === "boolean" ? record.truncated : undefined,
    ...(typeof record.shown_rows === "number" ? { shown_rows: record.shown_rows } : {}),
  };
}

function parseFailedResults(raw: unknown): ChatFailedResult[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      ...(typeof item.purpose === "string" ? { purpose: item.purpose } : {}),
      error_message: typeof item.error_message === "string" ? item.error_message : null,
    }));
}

/** Keeps every result set: an investigation's breakdowns (`additional_results`) and its
 * failed queries are what `DataTable` renders as sub-tabs and a warning. */
function parseTabularData(
  value: Record<string, unknown>,
): ChatTabularData | null {
  const primary = parseResultTable(value.tabular_data);
  if (!primary) {
    return null;
  }
  const record = value.tabular_data as Record<string, unknown>;
  const additional = Array.isArray(record.additional_results)
    ? record.additional_results
        .map((item) => parseResultTable(item))
        .filter((item): item is ChatResultTable => item !== null)
    : [];
  const failed = parseFailedResults(record.failed_results);
  return {
    ...primary,
    ...(additional.length > 0 ? { additional_results: additional } : {}),
    ...(failed.length > 0 ? { failed_results: failed } : {}),
  };
}

const VALID_CHART_TYPES = new Set<ChartType>([
  "line",
  "bar_vertical",
  "bar_horizontal",
  "area",
  "pie",
  "scatter",
  "combo",
]);

/**
 * Chart type names from before bars had an orientation. Chat history, app snapshots and
 * widget drafts saved then still say "bar", which always drew vertical columns.
 */
const LEGACY_CHART_TYPE_ALIASES: Record<string, ChartType> = { bar: "bar_vertical" };

/** The chart type `value` names, mapping legacy names; null when it names none. */
export function normalizeChartType(value: unknown): ChartType | null {
  if (typeof value !== "string") {
    return null;
  }
  const name = value.trim().toLowerCase();
  const chartType = LEGACY_CHART_TYPE_ALIASES[name] ?? name;
  return VALID_CHART_TYPES.has(chartType as ChartType) ? (chartType as ChartType) : null;
}

function parseChartDimension(value: unknown): ChartDimension | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.key !== "string" || !record.key.trim()) {
    return null;
  }
  const axisType = record.type;
  return {
    key: record.key,
    label: typeof record.label === "string" ? record.label : record.key,
    type:
      axisType === "time" || axisType === "category" || axisType === "number"
        ? axisType
        : "category",
  };
}

function parseStackGroup(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function parseChartMeasures(value: unknown): ChartMeasure[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    )
    .map((item) => {
      const encoding =
        item.encoding === "line" || item.encoding === "bar"
          ? item.encoding
          : null;
      return {
        key: typeof item.key === "string" ? item.key : "",
        label: typeof item.label === "string" ? item.label : null,
        encoding: encoding as MeasureEncoding | null,
        predicted: typeof item.predicted === "boolean" ? item.predicted : null,
        y_axis_index:
          item.y_axis_index === 0 || item.y_axis_index === 1
            ? (item.y_axis_index as 0 | 1)
            : null,
        stack_group: parseStackGroup(item.stack_group),
      };
    })
    .filter((item) => item.key.length > 0);
}

function parseLegacySeriesAsMeasures(
  value: unknown,
  chartType: ChartType,
): ChartMeasure[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    )
    .map((item) => {
      const key = typeof item.key === "string" ? item.key : "";
      let encoding: MeasureEncoding | null = null;
      if (chartType === "combo") {
        const seriesType = item.type;
        if (seriesType === "line" || seriesType === "bar") {
          encoding = seriesType;
        }
      }
      return {
        key,
        label: typeof item.label === "string" ? item.label : null,
        encoding,
      };
    })
    .filter((item) => item.key.length > 0);
}

function parseGeneralizedChartDefinition(
  value: unknown,
): GeneralizedChartDefinition | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;

  const recommendedChartType = normalizeChartType(
    record.recommended_chart_type ?? record.chart_type,
  );
  if (!recommendedChartType) {
    return null;
  }

  const dimension = parseChartDimension(record.dimension ?? record.x);
  if (!dimension) {
    return null;
  }

  let measures = parseChartMeasures(record.measures);
  if (measures.length === 0) {
    measures = parseLegacySeriesAsMeasures(record.series, recommendedChartType);
  }
  if (measures.length === 0) {
    return null;
  }

  return {
    id: typeof record.id === "string" ? record.id : "chart_1",
    title: typeof record.title === "string" ? record.title : null,
    recommended_chart_type: recommendedChartType,
    is_trend: typeof record.is_trend === "boolean" ? record.is_trend : false,
    dimension,
    measures,
    rationale: typeof record.rationale === "string" ? record.rationale : null,
    stack_dimension: parseChartDimension(record.stack_dimension),
  };
}

const VALID_CARD_FORMATS = new Set<CardFormat>(["number", "text"]);

function parseCardDefinition(value: unknown): CardDefinition | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.id !== "string" || typeof record.column !== "string") {
    return null;
  }
  const format = record.format;
  if (
    typeof format !== "string" ||
    !VALID_CARD_FORMATS.has(format as CardFormat)
  ) {
    return null;
  }
  return {
    id: record.id,
    title: typeof record.title === "string" ? record.title : record.column,
    column: record.column,
    value: record.value,
    format: format as CardFormat,
  };
}

function parseCardDefinitions(value: unknown): CardDefinition[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => parseCardDefinition(item))
    .filter((item): item is CardDefinition => item !== null);
}

function extractSharedData(
  record: Record<string, unknown>,
  chartsRaw: unknown[],
): Record<string, unknown>[] {
  if (Array.isArray(record.data)) {
    return record.data.filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    );
  }
  if (Array.isArray(record.source_rows)) {
    return record.source_rows.filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    );
  }
  for (const chart of chartsRaw) {
    if (!chart || typeof chart !== "object") {
      continue;
    }
    const chartRecord = chart as Record<string, unknown>;
    if (Array.isArray(chartRecord.data)) {
      return chartRecord.data.filter(
        (item): item is Record<string, unknown> =>
          typeof item === "object" && item !== null,
      );
    }
  }
  return [];
}

function parseLegacyChartData(
  record: Record<string, unknown>,
): ChatChartCollection | null {
  const normalizedType = normalizeChartType(record.chart_type);
  if (!normalizedType) {
    return null;
  }
  const xKey = typeof record.x === "string" ? record.x : null;
  const yKeys = Array.isArray(record.y)
    ? record.y.filter((item): item is string => typeof item === "string")
    : [];
  if (!xKey || yKeys.length === 0) {
    return null;
  }
  return {
    version: 2,
    data: [],
    charts: [
      {
        id: "chart_1",
        recommended_chart_type: normalizedType,
        title: typeof record.title === "string" ? record.title : null,
        is_trend: normalizedType === "line" || normalizedType === "area",
        dimension: { key: xKey, label: xKey, type: "category" },
        measures: yKeys.map((key) => ({
          key,
          label: key,
          encoding:
            normalizedType === "combo" ? ("line" as MeasureEncoding) : null,
        })),
      },
    ],
  };
}

function parseChartData(
  value: Record<string, unknown>,
): ChatChartCollection | null {
  const raw = value.chart_data;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  return parseChartCollectionFromRaw(raw);
}

function parseGeneratedQueries(
  value: Record<string, unknown>,
): GeneratedQuery[] | null {
  const raw = value.generated_queries;
  if (Array.isArray(raw)) {
    return raw.filter(
      (item): item is GeneratedQuery =>
        typeof item === "object" && item !== null,
    );
  }
  if (raw && typeof raw === "object") {
    const queries = (raw as Record<string, unknown>).queries;
    if (Array.isArray(queries)) {
      return queries.filter(
        (item): item is GeneratedQuery =>
          typeof item === "object" && item !== null,
      );
    }
  }
  return null;
}

export function parseGeneratedQueriesFromRaw(raw: unknown): GeneratedQuery[] | null {
  return parseGeneratedQueries({ generated_queries: raw });
}

function parseClarificationRequest(
  value: ClarificationRequest | Record<string, unknown> | null | undefined,
): ClarificationRequest | null {
  return normalizeClarificationRequest(value);
}

const VALID_ANSWER_CONFIDENCE_LEVELS = new Set<AnswerConfidenceLevel>([
  "high",
  "medium",
  "low",
]);

function parseAnswerConfidence(
  value: Record<string, unknown>,
): AnswerConfidence | null {
  const raw = value.answer_confidence;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const level = record.level;
  if (
    typeof record.score !== "number" ||
    typeof level !== "string" ||
    !VALID_ANSWER_CONFIDENCE_LEVELS.has(level as AnswerConfidenceLevel)
  ) {
    return null;
  }
  return {
    score: record.score,
    level: level as AnswerConfidenceLevel,
    signals: Array.isArray(record.signals)
      ? record.signals.filter(
          (item): item is string => typeof item === "string",
        )
      : [],
  };
}

const VALID_DOCUMENT_AGREEMENTS = new Set<DocumentAgreement>([
  "supports",
  "complements",
  "conflicts",
  "document_only",
  "not_relevant",
]);

function parseDocumentAgreement(
  value: Record<string, unknown>,
): DocumentAgreement | null {
  const raw = value.document_agreement;
  if (
    typeof raw !== "string" ||
    !VALID_DOCUMENT_AGREEMENTS.has(raw as DocumentAgreement)
  ) {
    return null;
  }
  return raw as DocumentAgreement;
}

function parseDocumentCitations(value: unknown): DocumentCitation[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const sourceFile = record.source_file;
    if (typeof sourceFile !== "string" || sourceFile.length === 0) {
      return [];
    }
    return [
      {
        sourceFile,
        page: typeof record.page === "number" ? record.page : null,
      },
    ];
  });
}

/**
 * Only entries carrying both values are kept — a one-sided entry says nothing the answer does not
 * already say, and rendering it would put a value with no counterpart in front of the reader.
 */
function parseDocumentConflicts(
  value: Record<string, unknown>,
): DocumentConflict[] {
  const raw = value.conflicts;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const subject = record.subject;
    const structuredValue = record.structured_value;
    const documentValue = record.document_value;
    if (
      typeof subject !== "string" ||
      typeof structuredValue !== "string" ||
      typeof documentValue !== "string"
    ) {
      return [];
    }
    return [
      {
        subject,
        structuredValue,
        documentValue,
        structuredSource:
          typeof record.structured_source === "string"
            ? record.structured_source
            : null,
        documentSources: parseDocumentCitations(record.document_sources),
        documentStatedSource:
          typeof record.document_stated_source === "string"
            ? record.document_stated_source
            : null,
        likelyCause:
          typeof record.likely_cause === "string" ? record.likely_cause : null,
        samePeriod: record.same_period === true,
      },
    ];
  });
}

/**
 * Cited sources with their page and preview location. A ref with no `source_file` is dropped: it
 * cannot be labelled, linked, or previewed, so there is nothing to render for it.
 */
function parseDocumentSourceRefs(
  value: Record<string, unknown>,
): DocumentSourceRef[] {
  const raw = value.document_source_refs;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const sourceFile = record.source_file;
    if (typeof sourceFile !== "string" || sourceFile.length === 0) {
      return [];
    }
    return [
      {
        sourceFile,
        page: typeof record.page === "number" ? record.page : null,
        docTitle:
          typeof record.doc_title === "string" ? record.doc_title : null,
        timeline: typeof record.timeline === "string" ? record.timeline : null,
        isImage: record.is_image === true,
        // Only a `data:` URI is accepted. This value goes straight into an `img src`, so anything
        // else — an `http(s)` URL, a `javascript:` scheme — is a request to load or run something
        // the pipeline did not put there.
        previewImage:
          typeof record.preview_image === "string" &&
          record.preview_image.startsWith("data:image/")
            ? record.preview_image
            : null,
      },
    ];
  });
}

function isLearningHistoryOwnerType(value: unknown): value is LearningHistoryOwnerType {
  return value === "measure" || value === "dimension";
}

function parseLearningHistoryAlternatives(raw: unknown): LearningHistoryAlternative[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    if (
      !isLearningHistoryOwnerType(record.owner_type) ||
      typeof record.owner_key !== "string" ||
      typeof record.label !== "string" ||
      typeof record.rewritten_question !== "string" ||
      !record.rewritten_question.trim()
    ) {
      return [];
    }
    return [
      {
        ownerType: record.owner_type,
        ownerKey: record.owner_key,
        label: record.label,
        rewrittenQuestion: record.rewritten_question,
      },
    ];
  });
}

/**
 * Terms auto-resolved from the user's Learning History. An entry missing the ids the override
 * call needs is dropped: the note would offer a "Change" the backend could not act on.
 */
export function parseLearningHistoryResolutions(raw: unknown): LearningHistoryResolution[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const record = item as Record<string, unknown>;
    const confidenceLabel = record.confidence_label;
    if (
      typeof record.term !== "string" ||
      !record.term.trim() ||
      !isLearningHistoryOwnerType(record.owner_type) ||
      typeof record.owner_key !== "string" ||
      typeof record.label !== "string" ||
      !record.label.trim() ||
      typeof record.insight_id !== "number" ||
      typeof record.domain_id !== "number"
    ) {
      return [];
    }
    return [
      {
        term: record.term,
        ownerType: record.owner_type,
        ownerKey: record.owner_key,
        label: record.label,
        confidenceLabel: VALID_ANSWER_CONFIDENCE_LEVELS.has(
          confidenceLabel as AnswerConfidenceLevel,
        )
          ? (confidenceLabel as AnswerConfidenceLevel)
          : LEARNING_INSIGHT_PRIORITY_LABEL.MEDIUM,
        insightId: record.insight_id,
        domainId: record.domain_id,
        // Absent on answers recorded before domain learnings existed, which were all the user's.
        scope:
          record.scope === LEARNING_RESOLUTION_SCOPE.DOMAIN
            ? LEARNING_RESOLUTION_SCOPE.DOMAIN
            : LEARNING_RESOLUTION_SCOPE.USER,
        alternatives: parseLearningHistoryAlternatives(record.alternatives),
      },
    ];
  });
}

/** The answer's `learning_suggestion` (or the endpoint's reply), or null when malformed. */
export function parseLearningSuggestion(raw: unknown): LearningSuggestion | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (typeof record.statement !== "string" || !record.statement.trim()) {
    return null;
  }
  const decision = Object.values(LEARNING_SUGGESTION_DECISION).find(
    (value) => value === record.decision,
  );
  const domainDecision = Object.values(LEARNING_SUGGESTION_DOMAIN_DECISION).find(
    (value) => value === record.domain_decision,
  );
  return {
    statement: record.statement,
    decision: decision ?? null,
    domainDecision: domainDecision ?? null,
  };
}

function clarificationFromVerboseSteps(
  verboseLog: PipelineVerboseStep[],
): ClarificationRequest | null {
  for (let index = verboseLog.length - 1; index >= 0; index -= 1) {
    const step = verboseLog[index];
    if (step.eventType !== "clarification_required") {
      continue;
    }
    const payload = step as PipelineVerboseStep & {
      request?: ClarificationRequest;
    };
    if (payload.request) {
      return parseClarificationRequest(payload.request);
    }
  }
  return null;
}

export function mapSessionTurnItemToConversationTurn(
  item: SessionTurnItem,
): ConversationTurn {
  const text = parseResponseText(item.assistant_response);
  const responseObject =
    item.assistant_response && typeof item.assistant_response === "object"
      ? item.assistant_response
      : null;
  const followUpQuestions = responseObject
    ? parseFollowUpQuestions(responseObject)
    : [];
  const answerKind = responseObject
    ? parseAnswerKind(responseObject)
    : undefined;
  const summary = responseObject
    ? parseStringField(responseObject, "summary")
    : null;
  const insights = responseObject
    ? parseStringArray(responseObject, "insights")
    : null;
  const why = responseObject ? parseStringField(responseObject, "why") : null;
  const whyPoints = responseObject
    ? parseStringArray(responseObject, "why_points")
    : null;
  const action = responseObject
    ? parseStringField(responseObject, "action")
    : null;
  const actionPoints = responseObject
    ? parseStringArray(responseObject, "action_points")
    : null;
  const sources = responseObject
    ? parseCitedSources(responseObject.sources)
    : [];
  const businessImpact = responseObject
    ? parseStringField(responseObject, "business_impact")
    : null;
  const businessImpactUpdated = responseObject?.business_impact_updated === true;
  const answerConfidence = responseObject
    ? parseAnswerConfidence(responseObject)
    : null;
  const documentAgreement = responseObject
    ? parseDocumentAgreement(responseObject)
    : null;
  const documentConflicts = responseObject
    ? parseDocumentConflicts(responseObject)
    : [];
  const documentSources = responseObject
    ? parseStringArray(responseObject, "document_sources")
    : null;
  const documentSourceRefs = responseObject
    ? parseDocumentSourceRefs(responseObject)
    : [];
  const learningHistoryResolutions = responseObject
    ? parseLearningHistoryResolutions(responseObject.learning_history_resolutions)
    : [];
  const learningSuggestion = responseObject
    ? parseLearningSuggestion(responseObject.learning_suggestion)
    : null;
  const tabularData =
    parseTabularDataFromRaw(item.tabular_data) ??
    (responseObject ? parseTabularData(responseObject) : null);
  const chartData =
    parseChartCollectionFromRaw(item.chart_data) ??
    (responseObject ? parseChartData(responseObject) : null);
  const generatedQueries = responseObject
    ? parseGeneratedQueries(responseObject)
    : null;
  // Answer text is persisted as `token` steps so the stream can be replayed after a reconnect.
  // They are chunks of the response, not pipeline steps, and the live stream already keeps them
  // out of the log -- drop them here too so "How it was generated" reads the same either way.
  const verboseLog = (item.verbose_steps ?? [])
    .map(mapVerboseStepItem)
    .filter((step) => !HISTORY_HIDDEN_STEP_TYPES.has(step.eventType));
  const status = normalizeTurnStatus(item.response_status);
  const isInProgress =
    status === "pending" ||
    status === "streaming" ||
    status === "waiting_for_clarification";
  const isTerminated = status === "terminated";
  const clarificationRequest =
    parseClarificationRequest(item.clarification_request) ??
    clarificationFromVerboseSteps(verboseLog);
  const feedback = parseChatTurnFeedbackFromRaw(item.feedback);
  // A page reloaded mid-turn resumes its elapsed-time clock from when the question was asked,
  // not from the reload.
  const createdAtMs = item.created_at ? Date.parse(item.created_at) : Number.NaN;
  const startedAt = Number.isNaN(createdAtMs) ? undefined : createdAtMs;
  const completedAtMs = item.completed_at ? Date.parse(item.completed_at) : Number.NaN;

  return {
    turnId: item.turn_id,
    turnIndex: item.turn_index,
    userQuestion: item.user_question,
    clarificationRequest,
    feedback,
    ...(startedAt !== undefined ? { startedAt } : {}),
    ...(Number.isNaN(completedAtMs) ? {} : { completedAt: completedAtMs }),
    // Time spent waiting on clarification answers stays out of the reloaded clock too.
    ...turnClockFromSteps(verboseLog, status === "waiting_for_clarification"),
    assistantData:
      text !== null ||
      verboseLog.length > 0 ||
      isInProgress ||
      isTerminated ||
      clarificationRequest
        ? {
            text: text ?? "",
            summary,
            insights,
            why,
            action,
            businessImpact,
            followUpQuestions,
            verboseLog,
            answerKind,
            tabularData,
            chartData,
            generatedQueries,
            clarificationRequest,
            answerConfidence,
            // Omitted entirely rather than defaulted, so a turn that drew on no documents
            // carries exactly the shape it always has. Same for the action fields: a turn
            // answered before action points existed is indistinguishable from one today.
            ...(whyPoints ? { whyPoints } : {}),
            ...(actionPoints ? { actionPoints } : {}),
            ...(sources.length > 0 ? { sources } : {}),
            ...(documentAgreement ? { documentAgreement } : {}),
            ...(documentConflicts.length > 0 ? { documentConflicts } : {}),
            ...(documentSources && documentSources.length > 0
              ? { documentSources }
              : {}),
            ...(documentSourceRefs.length > 0 ? { documentSourceRefs } : {}),
            ...(learningHistoryResolutions.length > 0
              ? { learningHistoryResolutions }
              : {}),
            ...(learningSuggestion ? { learningSuggestion } : {}),
            ...(businessImpactUpdated ? { businessImpactUpdated } : {}),
          }
        : null,
    status,
  };
}

function normalizeTurnStatus(status: string): TurnResponseStatus {
  if (
    status === "pending" ||
    status === "streaming" ||
    status === "waiting_for_clarification" ||
    status === "complete" ||
    status === "error" ||
    status === "terminated"
  ) {
    return status;
  }
  return "complete";
}
